import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { syncAuthData, isAuthenticated } from '../utils/AuthUtils';

// Protected Route component that checks for authentication
const ProtectedRoute = ({ children }) => {
  const { isLoggedIn, loading } = useAuth();
  const location = useLocation();

  useEffect(() => {
    // Try to sync auth data from cookies to localStorage when route loads
    syncAuthData();
  }, []);

  // Show loading state if auth is still being checked
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  // Check both context state and direct cookie/localStorage check
  const authenticated = isLoggedIn || isAuthenticated();

  if (!authenticated) {
    // Redirect to login if not authenticated, preserving the intended destination
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// Public route that redirects authenticated users to home
const PublicRoute = ({ children }) => {
  const { isLoggedIn, loading } = useAuth();
  const location = useLocation();
  
  useEffect(() => {
    // Try to sync auth data from cookies to localStorage when route loads
    syncAuthData();
  }, []);

  // Show loading state if auth is still being checked
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  // Check both context state and direct cookie/localStorage check
  const authenticated = isLoggedIn || isAuthenticated();
  
  // If user is already logged in and tries to access login or signup page, redirect to home
  if (authenticated && (location.pathname === '/login' || location.pathname === '/signup')) {
    // Redirect to the page they were trying to access before, or home
    const from = location.state?.from?.pathname || '/home';
    return <Navigate to={from} replace />;
  }

  return children;
};

export { ProtectedRoute, PublicRoute };